import { Button } from "@/components/ui/button";
import Icons from "../../icons";
import { Login } from "./Login";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HCaptcha from '@hcaptcha/react-hcaptcha';

export const Register = ({ children }: { children: React.ReactNode }) => {
  const [[username, setUsername], [usernameError, setUsernameError]] = [useState(""), useState("")];
  const [[email, setEmail], [emailError, setEmailError]] = [useState(""), useState("")];
  const [[password, setPassword], [passwordError, setPasswordError]] = [useState(""), useState("")];
  const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(true);
  const [isPolicyChecked, setIsPolicyChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<any>(null);
  const supportedPlatforms: { name: string; color: string; icon: string; redirect: string; }[] = [
  ];


  useEffect(() => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    let emailError = true;
    let passwordError = true;
    let usernameError = true;

    if (String(email).length === 0 || emailRegex.test(email)) {
      setEmailError(""); emailError = false;
    } else {
      setEmailError("Please enter a valid email address"); emailError = true;
    }

    if (String(password).length < 6 && String(password).length > 0) {
      setPasswordError("Password must be at least 6 characters long"); passwordError = true;
    } else {
      setPasswordError(""); passwordError = false;
    }

    if (username.startsWith(" ") || username.endsWith(" ")) {
      setUsernameError("Username cannot start or end with a space"); usernameError = true;
    } else {
      setUsernameError(""); usernameError = false;
    }

    if (String(username).length > 12) {
      setUsernameError("Username must be less than 12 characters long"); usernameError = true;
    } else {
      setUsernameError(""); usernameError = false;
    }

    if (!emailError && !passwordError && !usernameError && email.length > 0 && password.length > 0 && username.length > 0 && isPolicyChecked && captchaToken) {
      setIsLoginButtonDisabled(false);
    } else {
      setIsLoginButtonDisabled(true);
    }
  }, [email, password, username, isPolicyChecked, captchaToken]);

  const RegisterButton = async () => {
    if (isLoginButtonDisabled) return;
    setIsLoginButtonDisabled(true);
    const response = await fetch("__SITE_BASE__/api/v1/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        username,
        email,
        password,
        captchaToken
      })
    });

    if (response.ok) window.location.reload();
    else {
      const data = await response.json();
      setIsLoginButtonDisabled(false);
      if (data.errors?.email) setEmailError(data.errors.email);
      if (data.errors?.password) setPasswordError(data.errors.password);
      if (data.errors?.username) setUsernameError(data.errors.username);
      if (captchaRef.current) captchaRef.current.resetCaptcha();

    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-brand-dark text-white border-0 max-w-96 sm:max-w-[425px]" onKeyPress={(e) => { if (e.key === "Enter") RegisterButton(); }}>
        <DialogHeader className="text-center items-center">
          <DialogTitle className="font-light">Sign up with Teamly</DialogTitle>
          <div className="flex flex-col space-y-4 w-full py-2">
            <>
              {supportedPlatforms.map((platform, index) => (
                <Button
                  key={index}
                  className={`flex w-full items-center justify-start rounded-lg bg-gradient-to-br from-${platform.color}-500 to-${platform.color}-600 shadow-sm shadow-${platform.color}-500`}
                  onClick={() => window.location.href = platform.redirect}
                >
                  <span>
                    <Icons name={platform.icon} size="24" color="white" />
                  </span>
                  <div className="flex-grow">
                    <span className="text-white font-semibold flex justify-center gap-1">
                      Continue with
                      <span className="text-white font-bold">{platform.name}</span>
                    </span>
                  </div>
                </Button>
              ))}
            </>
          </div>
          {supportedPlatforms.length > 0 && (
            <div className="flex items-center justify-center my-4 w-full">
              <div className="flex-grow border-t border-gray-700"></div>
              <div className="text-white ml-2 mr-2">or</div>
              <div className="flex-grow border-t border-gray-700"></div>
            </div>
          )}
        </DialogHeader>
        <DialogDescription>
          <div className="space-y-4">
            <div className="space-y-2 flex-col flex">
              <Label>Username</Label>
              {usernameError && <span className="text-red-500 text-sm">{usernameError}</span>}
              <Input
                className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                placeholder="Username"
                type="text"
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="false"
              />
            </div>
            <div className="space-y-2 flex-col flex">
              <Label>Email</Label>
              {emailError && <span className="text-red-500 text-sm">{emailError}</span>}
              <Input
                className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                placeholder="Email"
                type="email"
                autoComplete="false"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2 flex-col flex">
              <Label>Password</Label>
              {passwordError && <span className="text-red-500 text-sm">{passwordError}</span>}
              <Input
                className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                type="password" placeholder="Password" onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-start mt-4 gap-2">
            <input type="checkbox" className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green" onChange={() => setIsPolicyChecked(!isPolicyChecked)} />
            <span className="text-gray-300">I agree to the <a className="text-brand-green cursor-pointer" onClick={() => window.open("__SITE_BASE__/tos", "_blank")} >Terms of Service</a> and <a className="text-brand-green cursor-pointer"  onClick={() => window.open("__SITE_BASE__/privacy", "_blank")}>Privacy Policy</a></span>
          </div>
          <div className="flex items-center justify-center mt-4">
            <HCaptcha
              sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              theme="dark"
              ref={captchaRef}
            />
          </div>
          <div className="flex items-center justify-center mt-2">
            <Button className="bg-brand-green px-5 py-2 w-full rounded-lg text-white font-semibold hover:bg-green-500" disabled={isLoginButtonDisabled} onClick={RegisterButton}>
              Register
            </Button>
          </div>
        </DialogDescription>
        <DialogFooter className="text-center mb-2 border-t border-gray-700 flex justify-center sm:justify-center items-center">
          <div className="text-gray-300 mt-2 flex justify-center">
            <span>Already have an account? </span>
            <Login>
              <a className="text-brand-green ml-1 hover:cursor-pointer">Log in</a>
            </Login>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};